import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
} from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { CustomTextField } from "../formsComponents";
import { api } from "../../api/api";
import { ISubscriptionPlan } from "../../redux/subscription/subscription.types";

export interface ISubscriptionPlanAutoSearchProps {
  label?: string;
  placeholder?: string;
  value: string | null;
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  onSelect: (value: ISubscriptionPlan | null) => void;
  isHighlighted?: boolean;
}

export const SubscriptionPlanAutoSearch = forwardRef<
  {
    clear: () => void;
  },
  ISubscriptionPlanAutoSearchProps
>((props, ref) => {
  const { value, disabled, label, placeholder = "Search plan name...", onSelect, isHighlighted } = props;
  const [options, setOptions] = useState<readonly ISubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/subscription/get-subscription-plan?status=PUBLISH`;
      const res = await api.get(url);
      const data: ISubscriptionPlan[] = res.data?.data || res.data || [];
      setOptions(data);
    } catch (error) {
      console.error("❌ SubscriptionPlanAutoSearch API error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const clear = useCallback(() => {
    setKey((prev) => prev + 1);
    onSelect(null);
  }, [onSelect]);

  useImperativeHandle(ref, () => ({
    clear,
  }));

  const selectedOption =
    options.find(
      (option) =>
        option.plan_name === value || option.subscription_plan_uuid === value
    ) || null;

  return (
    <Autocomplete
      key={key}
      fullWidth
      sx={{
        ".MuiOutlinedInput-root": {
          paddingTop: "2px",
          paddingBottom: "2px",
          fontSize: "0.8rem",
          color: "rgb(38, 38, 38)",
          ...(isHighlighted && {
            borderColor: "warning.main",
            borderWidth: "1px",
            borderStyle: "solid",
          }),
        },
      }}
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(option) => option.plan_name || ""}
      isOptionEqualToValue={(option, val) =>
        option.plan_name === val.plan_name ||
        option.subscription_plan_uuid === val.subscription_plan_uuid
      }
      value={selectedOption}
      onChange={(_e, newValue) => {
        onSelect(newValue);
      }}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label={label || "Plan Name"}
          placeholder={placeholder}
          size="small"
          fullWidth
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
});

SubscriptionPlanAutoSearch.displayName = "SubscriptionPlanAutoSearch";
